import React from 'react';
import { Link, Navigate } from 'react-router-dom'
import { Table, Tag, Card, Row, Col, Form, Statistic, Button, Space, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import FormModal, { formatGNF } from '../components/modalDepotForm'
import FormClientModal from '../../customers/components/FormModal';
import axios from '../../services/axios'
import SelectSearchInput from '../../utils/components/selectSearchInput';
import DatePickerCustom from '../../utils/components/datePicker';
import { exportToExcel } from '../../utils/exportExcel';
import moment from 'moment';

const montantDuOf = (row: any) => Number(row.montant_du ?? row.montant_sortie) || 0;
const margeOf = (row: any) => (Number(row.montant_sortie) || 0) - montantDuOf(row);

const Depot = () => {
  const [visible, setVisible] = React.useState(false);
  const [visibleClient, setVisibleClient] = React.useState(false);
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const [data, setData] = React.useState([]);
  const [redirect, setRedirect] = React.useState(false)
  const [loading, setLoading] = React.useState(false);
  const [devises, setDevises] = React.useState([])
  const [clients, setClients] = React.useState([])
  const [dateStart, setDateStart] = React.useState(moment(new Date()).format('YYYY-MM-DD'));
  const [dateEnd, setDateEnd] = React.useState(moment(new Date()).format('YYYY-MM-DD'));
  const [idDevise, setIdDevise] = React.useState(null)

  const [form] = Form.useForm();

  React.useEffect(() => {
    fetchData(dateStart, dateEnd, null)
    getDevise()
    getClient()
  }, [])

  const getClient = () => {
    axios.get('/clients')
      .then(response => setClients(response.data));
  }

  const getDevise = () => {
    axios.get('/devises')
      .then(response => setDevises(response.data));
  }

  const fetchData = (start: string, end: string, devise: any) => {
    setLoading(true);
    const url = devise
      ? `/operations/type/depot/${start}/${end}/${devise}`
      : `/operations/type/depot/${start}/${end}`;
    axios.get(url)
      .then(response => {
        setData(response.data)
        setLoading(false)
      })
      .catch(() => setLoading(false));
  };

  const onCreate = (values: any) => {
    setConfirmLoading(true);
    return axios.post('/operations/new', values)
      .then(function () {
        setRedirect(true)
        fetchData(dateStart, dateEnd, idDevise)
        setConfirmLoading(false);
        setVisible(false);
        form.resetFields();
      })
      .catch(function (error) {
        setConfirmLoading(false);
        message.error(error.response?.data || "Erreur d'insertion");
        throw error;
      });
  };

  const onClientCreate = (values: any) => {
    axios.post('/clients/new', values)
      .then(function () {
        getClient()
        form.resetFields();
        setVisibleClient(false);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const onDeviseFilter = (value: any) => {
    setIdDevise(value);
    fetchData(dateStart, dateEnd, value)
  }

  const onDateChange = (dates: any, dateStrings: any) => {
    setDateStart(dateStrings[0])
    setDateEnd(dateStrings[1])
    fetchData(dateStrings[0], dateStrings[1], idDevise)
  }

  const totalGNF = data.reduce((acc: number, row: any) => acc + (Number(row.montant_sortie) || 0), 0);
  const totalDu = data.reduce((acc: number, row: any) => acc + montantDuOf(row), 0);

  const handleExport = () => {
    const rows = data.map((row: any, index: number) => ({
      'N°': index + 1,
      "Date d'entrée": row.date_operation,
      'Client': `${row.client?.prenom ?? ''} ${row.client?.nom ?? ''}`.trim(),
      'Déposant': row.nom_deposant || 'Par le client lui même',
      'Montant reçu': Number(row.montant) || 0,
      'Devise': row.devise?.code,
      'Taux du jour': Number(row.taux) || 0,
      'Montant en GNF': Number(row.montant_sortie) || 0,
      'Taux à payer': Number(row.taux_payer ?? row.taux) || 0,
      'Montant dû': montantDuOf(row),
      'Marge bénéficiaire': margeOf(row),
      'N° reçu': row.numero_recu,
      'Créé par': row.user ? `${row.user.prenom} ${row.user.nom}` : '-',
    }));
    exportToExcel(`entrees_${dateStart}_${dateEnd}`, 'Entrées', rows);
  };

  const columns = [
    {
      title: "N°",
      key: "index",
      render: (row: any, value: any, index: number = 0) => <>{index + 1}</>,
    },
    {
      title: "Date d'entrée",
      dataIndex: 'date_operation',
      key: 'date_operation'
    },
    {
      title: 'Client',
      key: 'client',
      width: 180,
      render: (record: any) => (
        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {record.client?.prenom} {record.client?.nom}
        </span>
      ),
    },
    {
      title: 'Déposant',
      key: 'nom_deposant',
      render: (row: any) => <>{row.nom_deposant ? row.nom_deposant : 'Par le client lui même'}</>,
    },
    {
      title: 'Montant reçu',
      dataIndex: 'montant',
      align: 'right',
      key: 'montant',
      render: (montant: any) => formatGNF(montant),
    },
    {
      title: 'Devise',
      key: 'devise',
      align: 'center',
      render: (row: any) => <Tag>{row.devise?.code}</Tag>,
    },
    {
      title: 'Taux du jour',
      dataIndex: 'taux',
      align: 'right',
      key: 'taux',
      render: (taux: any) => Number(taux).toLocaleString('fr-FR', { maximumFractionDigits: 2 }),
    },
    {
      title: 'Montant en GNF',
      dataIndex: 'montant_sortie',
      align: 'right',
      key: 'montant_sortie',
      render: (montant: any) => <b>{formatGNF(montant)} GNF</b>,
    },
    {
      title: 'Taux à payer',
      dataIndex: 'taux_payer',
      align: 'right',
      key: 'taux_payer',
      render: (taux: any, row: any) =>
        Number(taux ?? row.taux).toLocaleString('fr-FR', { maximumFractionDigits: 2 }),
    },
    {
      title: 'Montant dû',
      key: 'montant_du',
      align: 'right',
      render: (row: any) => <>{formatGNF(montantDuOf(row))} GNF</>,
    },
    {
      title: 'Marge bénéficiaire',
      key: 'benefice',
      align: 'right',
      render: (row: any) => {
        const marge = margeOf(row);
        return <Tag color={marge > 0 ? 'green' : marge < 0 ? 'red' : 'default'}>{formatGNF(marge)} GNF</Tag>;
      },
    },
    {
      title: 'N° reçu',
      dataIndex: 'numero_recu',
      key: 'numero_recu'
    },
    {
      title: 'Créé par',
      key: 'user',
      render: (row: any) => <>{row.user ? `${row.user.prenom} ${row.user.nom}` : '-'}</>,
    },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'id',
      render: (id: any) => (
        <Button title="Editer">
          <Link to={`/depot/edit/${id}`}><EditOutlined /></Link>
        </Button>
      )
    },
  ];

  return <>
    <Card
      size="small"
      title={<h4>Entrées d'argent</h4>}
      extra={<Space>
        <Button onClick={handleExport}>Exporter Excel</Button>
        <Button type="primary" onClick={() => setVisible(true)}>Nouveau</Button>
      </Space>}
    >
      <Row justify='end'>
        <Form layout='inline'>
          <Form.Item label="Dates">
            <DatePickerCustom onChange={onDateChange} />
          </Form.Item>
          <Form.Item label='Devise'>
            <SelectSearchInput
              onChange={onDeviseFilter}
              data={devises}
              label='code'
              valueLabel='id'
              placeholder="Toutes les devises"
            />
          </Form.Item>
        </Form>
      </Row>
      <br />

      <Row gutter={16} justify='end' style={{ marginBottom: 16 }}>
        <Col>
          <Statistic title="Total en GNF" value={formatGNF(totalGNF)} suffix="GNF" valueStyle={{ fontSize: 16 }} />
        </Col>
        <Col>
          <Statistic title="Total dû" value={formatGNF(totalDu)} suffix="GNF" valueStyle={{ fontSize: 16 }} />
        </Col>
        <Col>
          <Statistic
            title="Bénéfice"
            value={formatGNF(totalGNF - totalDu)}
            suffix="GNF"
            valueStyle={{ color: '#3f8600', fontSize: 16 }}
          />
        </Col>
      </Row>

      <Table
        columns={columns as any}
        rowKey={(record: any) => record.id}
        dataSource={data}
        pagination={{ pageSize: 100 }}
        loading={loading}
        scroll={{ x: 'max-content' }}
      />

      <FormModal
        visible={visible}
        setClientVisible={() => setVisibleClient(true)}
        onCreate={onCreate}
        devises={devises}
        clients={clients}
        loading={confirmLoading}
        onCancel={() => setVisible(false)}
      />

      <FormClientModal
        visible={visibleClient}
        onCreate={onClientCreate}
        onCancel={() => setVisibleClient(false)}
      />
    </Card>

    {redirect ? (<Navigate to="/depot/" replace />) : ''}
  </>;
}

export default Depot;
